import { Add, Delete, Edit, Refresh } from '@mui/icons-material';
import {
  Badge,
  Box,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiIconButton from 'features/ui/base-components/pai-icon-button';
import {
  deleteEmailAccount,
  getEmailAccounts,
} from 'API/linkedin-accounts';
import { EmailAccount, LinkedinAccount } from './linkedin-accounts.types';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
// TODO: implement wizard component; temporary stub to avoid lint error
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import AddEditSenderWizard from './email-senders/add-edit-sender-wizard';

interface Props {
  account: LinkedinAccount;
  onEditLegacy?(): void;
  onRemoveLegacy?(): void;
}

/**
 * Displays and manages the list of e-mail sender accounts bound to the given LinkedIn account.
 *
 *  – Fetches list on mount and whenever `mutate()` is triggered.
 *  – Shows quota badge if all senders are exhausted.
 *  – Provides Add / Edit / Delete actions.
 */
const EmailSendersTab: React.FC<Props> = ({ account, onEditLegacy, onRemoveLegacy }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  // const handleOpenErrorDialog = useErrorStore((s) => s.handleOpenErrorDialog);

  const [senders, setSenders] = useState<EmailAccount[]>([]);
  // NEW: helper to create a pseudo-sender from the legacy emailConfig (if present)
  const legacySender: EmailAccount | null = React.useMemo(() => {
    const cfg = account.emailConfig;
    if (!cfg) return null;
    // Determine username / alias
    const username =
      (cfg.smtp as any)?.username ||
      (cfg.oauth as any)?.email ||
      (cfg.gmailOauth as any)?.email ||
      '';
    if (!username) return null;

    // Avoid duplicates: do not expose if already present in emailAccounts fetched from API
    // Guard against undefined usernames coming from backend by normalising to empty string first
    if (senders.some((s) => (s.username ?? '').toLowerCase() === username.toLowerCase())) return null;

    // Map config to sender type
    const type = cfg.smtp
      ? 'smtp'
      : cfg.oauth
        ? 'ms_o365'
        : cfg.gmailOauth
          ? 'gmail'
          : 'smtp';

    return {
      _id: 'legacy',
      type: type as any,
      username,
      dailyLimit: 0,
      dailySentCount: 0,
      accessRevoked: cfg.accessRevoked,
    } as EmailAccount;
  }, [account.emailConfig, senders]);

  // Combine API senders + legacy (if any)
  const displayedSenders = React.useMemo(() => {
    return legacySender ? [...senders, legacySender] : senders;
  }, [senders, legacySender]);

  const [loading, setLoading] = useState(false);
  const [wizard, setWizard] = useState<{ open: boolean; mode: 'add' | 'edit'; sender: EmailAccount | null }>({
    open: false,
    mode: 'add',
    sender: null,
  });

  const fetchSenders = async (disableLoading = false) => {
    try {
      if (!disableLoading) setLoading(true);
      const res = await getEmailAccounts( account._id);
      if (_.isArray(res)) {
        setSenders(res);
      } else if (res?.error) {
        console.error(res.error);
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (!disableLoading) setLoading(false);
    }
  };

  // initial fetch
  useEffect(() => {
    fetchSenders();
  }, []);

  const openAddWizard = () => setWizard({ open: true, mode: 'add', sender: null });
  const openEditWizard = (sender: EmailAccount) => setWizard({ open: true, mode: 'edit', sender });
  const closeWizard = () => setWizard({ open: false, mode: 'add', sender: null });

  const handleDelete = async (sender: EmailAccount) => {
    if (!window.confirm(t('delete') + ' ' + sender.username + '?')) return;
    try {
      setLoading(true);
      const res = await deleteEmailAccount( account._id, sender._id);
      if (res !== true && res?.error) {
        console.error(res.error);
      } else {
        await fetchSenders(true);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const quotaExhausted = senders.length > 0 && senders.every((s) => s.dailySentCount >= s.dailyLimit);

  return (
    <Box className={classes.root}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6">{t('emailSenders')}</Typography>
          {quotaExhausted && (
            <Badge color="error" variant="dot" />
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <PaiIconButton size="small" onClick={() => fetchSenders()} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : <Refresh fontSize="small" />}
          </PaiIconButton>
          <PaiButton text={t('add')} size="small" border="rounded" startIcon={<Add />} action={openAddWizard} />
        </Stack>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('alias')}</TableCell>
              <TableCell>{t('type')}</TableCell>
              <TableCell>{t('quota')}</TableCell>
              <TableCell>{t('status')}</TableCell>
              <TableCell sx={{ width: 120 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedSenders.map((s) => {
              const alias =
                s.username ||
                (s as any).smtp?.username ||
                (s as any).oauth?.email ||
                (s as any).gmailOauth?.email ||
                '-';
              const status = s.dmarcError ? 'DMARC' : s.accessRevoked ? 'Revoked' : s._id === 'legacy' ? 'Legacy' : 'OK';
              return (
                <TableRow key={s._id} hover>
                  <TableCell>{alias}</TableCell>
                  <TableCell>{s.type}</TableCell>
                  <TableCell>
                    {s.dailyLimit ? (
                      `${s.dailySentCount}/${s.dailyLimit}`
                    ) : (
                      '-' /* legacy sender has no quota info */
                    )}
                  </TableCell>
                  <TableCell>{status}</TableCell>
                  <TableCell>
                    {/* Hide edit/delete for legacy (deprecated emailConfig) */}
                    {s._id !== 'legacy' ? (
                      <Stack direction="row" spacing={1}>
                        <Tooltip title={t('edit')}>
                          <PaiIconButton size="small" onClick={() => openEditWizard(s)}>
                            <Edit fontSize="small" />
                          </PaiIconButton>
                        </Tooltip>
                        <Tooltip title={t('delete')}>
                          <PaiIconButton size="small" variant="error" onClick={() => handleDelete(s)}>
                            <Delete fontSize="small" />
                          </PaiIconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={1}>
                        {/* Legacy edit */}
                        {onEditLegacy && (
                          <Tooltip title={t('edit')}>
                            <PaiIconButton size="small" onClick={onEditLegacy}>
                              <Edit fontSize="small" />
                            </PaiIconButton>
                          </Tooltip>
                        )}
                        {onRemoveLegacy && (
                          <Tooltip title={t('delete')}>
                            <PaiIconButton size="small" variant="error" onClick={onRemoveLegacy}>
                              <Delete fontSize="small" />
                            </PaiIconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {displayedSenders.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {t('noEntries')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Wizard modal */}
      {wizard.open && (
        <AddEditSenderWizard
          open={wizard.open}
          mode={wizard.mode}
          account={account}
          sender={wizard.sender}
          onClose={closeWizard}
          onCompleted={() => {
            closeWizard();
            fetchSenders(true);
          }}
        />
      )}
    </Box>
  );
};

const useStyles = makeStyles()(() => ({
  root: {
    width: '100%',
  },
}));

export default EmailSendersTab;